import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { loadPdfjs } from "../lib/pdfjs_loader"

export default class extends Controller {
  static targets = [
    "leftPanel",
    "rightPanel",
    "pdfContainer",
    "pdfLoading",
    "textarea",
    "metaInput",
    "referenceInput",
    "saveButton",
    "status",
    "zoomLevel",
    "autoScrollToggle",
    "regenerateModal",
    "regenerateCheckboxes"
  ]

  static values = {
    pdfUrl: String,
    saveUrl: String,
    backUrl: String,
    version: Number,
    scale: { type: Number, default: 1 },
    minScale: { type: Number, default: 0.25 },
    maxScale: { type: Number, default: 4 },
    regenerateItems: Object,
    mediaType: String,
    currentContentType: String
  }

  connect() {
    this.dirty = false
    this.syncing = false
    this.scrollingToContent = false
    this.autoScrollEnabled = true
    this.pageCanvases = {}
    this.fitScale = 1
    this.originalValues = new Map()
    this.deletedItems = new Set()

    // Store original values
    this.textareaTargets.forEach((textarea) => {
      this.originalValues.set(textarea.dataset.index, textarea.value)
    })

    // Store original meta values
    this.originalMetaValues = new Map()
    this.metaInputTargets.forEach((input) => {
      const key = this.metaInputKey(input)
      this.originalMetaValues.set(key, input.value)
    })

    // Store original reference values
    this.originalRefValues = new Map()
    this.referenceInputTargets.forEach((input) => {
      this.originalRefValues.set(input.dataset.refIndex, input.value)
    })

    this.loadPdf()
    this.setupScrollSync()
    this.setupDirtyTracking()
    this.setupBeforeUnload()
  }

  disconnect() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
    }
    window.removeEventListener("beforeunload", this.beforeUnloadHandler)
  }

  // --- PDF Rendering ---

  async loadPdf() {
    if (!this.pdfUrlValue) return

    try {
      const response = await fetch(this.pdfUrlValue, { cache: "no-store" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const pdfData = await response.arrayBuffer()
      const pdfjsLib = await loadPdfjs()
      this.pdfDoc = await pdfjsLib.getDocument({ data: pdfData }).promise

      this.hidePdfLoading()
      await this.renderAllPages()
    } catch (error) {
      console.error("Error loading PDF:", error)
      this.showPdfError()
    }
  }

  async renderAllPages() {
    if (!this.pdfDoc) return

    const container = this.pdfContainerTarget
    const containerWidth = container.clientWidth - 32

    // Calculate fit-width scale from first page and store it
    const firstPage = await this.pdfDoc.getPage(1)
    const firstViewport = firstPage.getViewport({ scale: 1 })
    this.fitScale = containerWidth / firstViewport.width
    this.scaleValue = this.fitScale
    this.updateZoomDisplay()

    for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
      const page = await this.pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: 1 })
      const scale = this.scaleValue
      const scaledViewport = page.getViewport({ scale })

      const pageWrapper = document.createElement("div")
      pageWrapper.className = "edit-content-pdf-page"
      pageWrapper.dataset.pdfPage = pageNum

      const pageLabel = document.createElement("div")
      pageLabel.className = "edit-content-pdf-page-label"
      pageLabel.innerHTML = `<span>Page ${pageNum}</span><span class="edit-content-pdf-page-hint">Click to scroll</span>`
      pageWrapper.appendChild(pageLabel)

      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")
      const outputScale = window.devicePixelRatio || 1

      canvas.width = Math.floor(scaledViewport.width * outputScale)
      canvas.height = Math.floor(scaledViewport.height * outputScale)
      canvas.style.width = Math.floor(scaledViewport.width) + "px"
      canvas.style.height = Math.floor(scaledViewport.height) + "px"

      const transform = outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null

      await page.render({
        canvasContext: context,
        transform: transform,
        viewport: scaledViewport
      }).promise

      pageWrapper.appendChild(canvas)
      container.appendChild(pageWrapper)
      this.pageCanvases[pageNum] = pageWrapper

      pageWrapper.addEventListener("click", () => this.scrollToPageContent(pageNum))
    }
  }

  hidePdfLoading() {
    if (this.hasPdfLoadingTarget) {
      this.pdfLoadingTarget.style.display = "none"
    }
  }

  showPdfError() {
    this.hidePdfLoading()
    const container = this.pdfContainerTarget
    const error = document.createElement("div")
    error.className = "edit-content-pdf-error"
    error.textContent = "Failed to load PDF document"
    container.appendChild(error)
  }

  // --- Zoom Controls ---

  zoomIn() {
    const newScale = Math.min(this.scaleValue * 1.25, this.maxScaleValue)
    this.reRenderAllPages(newScale)
  }

  zoomOut() {
    const newScale = Math.max(this.scaleValue / 1.25, this.minScaleValue)
    this.reRenderAllPages(newScale)
  }

  fitScreen() {
    this.reRenderAllPages(this.fitScale)
  }

  async reRenderAllPages(scale) {
    if (!this.pdfDoc) return

    const oldScale = this.scaleValue
    this.scaleValue = scale
    this.updateZoomDisplay()

    const rightPanel = this.rightPanelTarget
    // Determine which page is currently visible and relative offset
    let visiblePage = 1
    let relativeOffset = 0
    const pages = rightPanel.querySelectorAll(".edit-content-pdf-page")
    for (const page of pages) {
      const rect = page.getBoundingClientRect()
      const panelRect = rightPanel.getBoundingClientRect()
      if (rect.bottom > panelRect.top) {
        visiblePage = parseInt(page.dataset.pdfPage, 10) || 1
        relativeOffset = (panelRect.top - rect.top) / rect.height
        break
      }
    }

    const container = this.pdfContainerTarget
    // Remove existing page elements
    container.querySelectorAll(".edit-content-pdf-page").forEach(el => el.remove())
    this.pageCanvases = {}

    for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
      const page = await this.pdfDoc.getPage(pageNum)
      const scaledViewport = page.getViewport({ scale: this.scaleValue })

      const pageWrapper = document.createElement("div")
      pageWrapper.className = "edit-content-pdf-page"
      pageWrapper.dataset.pdfPage = pageNum

      const pageLabel = document.createElement("div")
      pageLabel.className = "edit-content-pdf-page-label"
      pageLabel.innerHTML = `<span>Page ${pageNum}</span><span class="edit-content-pdf-page-hint">Click to scroll</span>`
      pageWrapper.appendChild(pageLabel)

      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")
      const outputScale = window.devicePixelRatio || 1

      canvas.width = Math.floor(scaledViewport.width * outputScale)
      canvas.height = Math.floor(scaledViewport.height * outputScale)
      canvas.style.width = Math.floor(scaledViewport.width) + "px"
      canvas.style.height = Math.floor(scaledViewport.height) + "px"

      const transform = outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null

      await page.render({
        canvasContext: context,
        transform: transform,
        viewport: scaledViewport
      }).promise

      pageWrapper.appendChild(canvas)
      container.appendChild(pageWrapper)
      this.pageCanvases[pageNum] = pageWrapper

      pageWrapper.addEventListener("click", () => this.scrollToPageContent(pageNum))
    }

    // Restore scroll position to the same page that was visible before zoom
    const targetPage = this.pageCanvases[visiblePage]
    if (targetPage) {
      const newTop = targetPage.offsetTop + (targetPage.offsetHeight * relativeOffset)
      rightPanel.scrollTop = newTop - rightPanel.offsetTop
    }
  }

  updateZoomDisplay() {
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = Math.round(this.scaleValue * 100) + "%"
    }
  }

  // --- Scroll to content from PDF click (right → left) ---

  scrollToPageContent(pageNum) {
    const leftPanel = this.leftPanelTarget
    const target = leftPanel.querySelector(`[data-page="${pageNum}"]`)
    if (!target) return

    this.scrollingToContent = true

    const done = () => {
      leftPanel.removeEventListener("scrollend", done)
      clearTimeout(fallback)
      // Small delay before re-enabling to let any queued throttle ticks pass
      setTimeout(() => { this.scrollingToContent = false }, 100)
    }
    leftPanel.addEventListener("scrollend", done)
    const fallback = setTimeout(done, 1500)

    leftPanel.scrollTo({
      top: target.offsetTop - leftPanel.offsetTop,
      behavior: "smooth"
    })
  }

  // --- Scroll Sync (left → right) ---

  toggleAutoScroll() {
    this.autoScrollEnabled = !this.autoScrollEnabled
    this.autoScrollToggleTarget.classList.toggle("active", this.autoScrollEnabled)
  }

  setupScrollSync() {
    this.scrollHandler = this.throttle(() => {
      if (!this.autoScrollEnabled || this.syncing || this.scrollingToContent) return
      this.syncScroll()
    }, 50)

    this.leftPanelTarget.addEventListener("scroll", this.scrollHandler)
  }

  syncScroll() {
    const leftPanel = this.leftPanelTarget
    const rightPanel = this.rightPanelTarget
    const panelTop = leftPanel.scrollTop
    const panelHeight = leftPanel.clientHeight

    // Find the page marker or item closest to the viewport center
    const centerY = panelTop + panelHeight / 3
    let currentPage = 1

    const markers = leftPanel.querySelectorAll("[data-page]")
    for (const marker of markers) {
      const markerTop = marker.offsetTop
      if (markerTop <= centerY) {
        const page = parseInt(marker.dataset.page, 10)
        if (page && page > 0) currentPage = page
      }
    }

    // Scroll right panel to the matching PDF page
    const targetCanvas = this.pageCanvases[currentPage]
    if (targetCanvas) {
      this.syncing = true
      rightPanel.scrollTo({
        top: targetCanvas.offsetTop - rightPanel.offsetTop,
        behavior: "smooth"
      })
      setTimeout(() => { this.syncing = false }, 300)
    }
  }

  // --- Dirty Tracking ---

  setupDirtyTracking() {
    this.textareaTargets.forEach((textarea) => {
      textarea.addEventListener("input", () => this.checkDirty())
    })
    this.metaInputTargets.forEach((input) => {
      input.addEventListener("input", () => this.checkDirty())
    })
    this.referenceInputTargets.forEach((input) => {
      input.addEventListener("input", () => this.checkDirty())
    })
  }

  metaInputKey(input) {
    const key = input.dataset.metaKey
    const index = input.dataset.metaIndex
    return index !== undefined ? `${key}:${index}` : key
  }

  checkDirty() {
    let hasChanges = this.deletedItems.size > 0
    this.textareaTargets.forEach((textarea) => {
      const original = this.originalValues.get(textarea.dataset.index)
      if (textarea.value !== original) {
        hasChanges = true
        textarea.classList.add("modified")
      } else {
        textarea.classList.remove("modified")
      }
    })

    this.metaInputTargets.forEach((input) => {
      const key = this.metaInputKey(input)
      const original = this.originalMetaValues.get(key)
      if (input.value !== original) {
        hasChanges = true
      }
    })

    this.referenceInputTargets.forEach((input) => {
      const original = this.originalRefValues.get(input.dataset.refIndex)
      if (input.value !== original) {
        hasChanges = true
        input.classList.add("modified")
      } else {
        input.classList.remove("modified")
      }
    })

    this.dirty = hasChanges
    this.saveButtonTarget.disabled = !hasChanges

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = hasChanges ? "Unsaved changes" : ""
      this.statusTarget.className = `edit-content-status ${hasChanges ? "has-changes" : ""}`
    }
  }

  // --- Before Unload ---

  setupBeforeUnload() {
    this.beforeUnloadHandler = (event) => {
      if (this.dirty) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", this.beforeUnloadHandler)
  }

  // --- Delete Item ---

  deleteItem(event) {
    if (!confirm("Are you sure you want to delete this item?")) return

    const index = event.currentTarget.dataset.index
    const itemEl = event.currentTarget.closest(".edit-content-item")

    this.deletedItems.add(parseInt(index, 10))

    // Remove associated textarea from original tracking
    this.originalValues.delete(index)

    if (itemEl) itemEl.remove()
    this.checkDirty()
  }

  // --- Cancel ---

  cancel(event) {
    if (this.dirty) {
      if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
        event.preventDefault()
      }
    }
  }

  // --- Save All ---

  async saveAll() {
    const payload = this._buildPayload()
    if (!payload) return

    const autoRegenerateTypes = ['full_audio', 'podcast']
    if (autoRegenerateTypes.includes(this.currentContentTypeValue)) {
      payload.regenerate_items = [this.currentContentTypeValue]
      await this._executeSave(payload)
      return
    }

    const items = this.mediaTypeValue ? (this.regenerateItemsValue[this.mediaTypeValue] || []) : []

    if (items.length > 0 && this.hasRegenerateModalTarget) {
      this._pendingPayload = payload
      this._populateRegenerateCheckboxes(items)
      this._regenerateModal = new Modal(this.regenerateModalTarget)
      this._regenerateModal.show()
      return
    }

    await this._executeSave(payload)
  }

  confirmRegenerate() {
    const checked = this.regenerateCheckboxesTarget.querySelectorAll('input[type="checkbox"]:checked')
    const regenerateItems = Array.from(checked).map(cb => cb.value)

    const payload = this._pendingPayload
    if (regenerateItems.length > 0) payload.regenerate_items = regenerateItems

    this._regenerateModal.hide()
    this._pendingPayload = null
    this._executeSave(payload)
  }

  skipRegenerate() {
    const payload = this._pendingPayload
    this._regenerateModal.hide()
    this._pendingPayload = null
    this._executeSave(payload)
  }

  _buildPayload() {
    const changedItems = []
    this.textareaTargets.forEach((textarea) => {
      const original = this.originalValues.get(textarea.dataset.index)
      if (textarea.value !== original) {
        changedItems.push({
          index: parseInt(textarea.dataset.index, 10),
          text: textarea.value
        })
      }
    })

    // Collect changed meta fields
    const changedMeta = {}
    this.metaInputTargets.forEach((input) => {
      const key = this.metaInputKey(input)
      const original = this.originalMetaValues.get(key)
      if (input.value !== original) {
        changedMeta[input.dataset.metaKey] = changedMeta[input.dataset.metaKey] || {}
        if (input.dataset.metaIndex !== undefined) {
          changedMeta[input.dataset.metaKey][input.dataset.metaIndex] = input.value
        } else {
          changedMeta[input.dataset.metaKey] = input.value
        }
      }
    })

    const hasMetaChanges = Object.keys(changedMeta).length > 0

    // Collect changed references
    const changedRefs = []
    this.referenceInputTargets.forEach((input) => {
      const original = this.originalRefValues.get(input.dataset.refIndex)
      if (input.value !== original) {
        changedRefs.push({
          index: parseInt(input.dataset.refIndex, 10),
          text: input.value
        })
      }
    })

    if (changedItems.length === 0 && !hasMetaChanges && changedRefs.length === 0 && this.deletedItems.size === 0) return null

    const payload = { version: this.versionValue }
    if (changedItems.length > 0) payload.items = changedItems
    if (hasMetaChanges) payload.meta = changedMeta
    if (changedRefs.length > 0) payload.references = changedRefs
    if (this.deletedItems.size > 0) payload.deleted_items = Array.from(this.deletedItems)

    return payload
  }

  _populateRegenerateCheckboxes(items) {
    const container = this.regenerateCheckboxesTarget
    container.innerHTML = ''

    items.forEach(item => {
      const wrapper = document.createElement('div')
      wrapper.className = 'mb-2'

      const label = document.createElement('label')
      label.className = 'checkbox md-checkbox option-button'

      const input = document.createElement('input')
      input.type = 'checkbox'
      input.className = 'checkbox-input'
      input.value = item.value
      input.checked = true

      const control = document.createElement('span')
      control.className = 'checkbox-control'

      const text = document.createElement('span')
      text.className = 'checkbox-label'
      text.textContent = item.label

      label.appendChild(input)
      label.appendChild(control)
      label.appendChild(text)
      wrapper.appendChild(label)
      container.appendChild(wrapper)
    })
  }

  async _executeSave(payload) {
    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Saving..."

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (data.success) {
        // Update original values to the saved values
        if (payload.items) {
          payload.items.forEach((item) => {
            this.originalValues.set(String(item.index), item.text)
          })
        }
        // Update original meta values
        this.metaInputTargets.forEach((input) => {
          const key = this.metaInputKey(input)
          this.originalMetaValues.set(key, input.value)
        })
        // Update original reference values
        this.referenceInputTargets.forEach((input) => {
          this.originalRefValues.set(input.dataset.refIndex, input.value)
        })
        // Update URLs and version for subsequent saves
        if (data.save_url) this.saveUrlValue = data.save_url
        if (data.back_url) {
          this.backUrlValue = data.back_url
          this.element.querySelectorAll('a[href]').forEach((link) => {
            if (link.getAttribute('href').includes('/review_content') || link.getAttribute('href').includes('/edit_content')) {
              link.setAttribute('href', data.back_url)
            }
          })
        }
        if (data.version) this.versionValue = data.version
        this.deletedItems.clear()
        this.dirty = false

        if (data.edit_url) {
          window.location.href = data.edit_url
          return
        }

        this.checkDirty()
        this.showSnackbar("Content saved successfully", "success")
      } else {
        this.showSnackbar("Failed to save: " + (data.errors || []).join(", "), "error")
        this.saveButtonTarget.disabled = false
      }
    } catch (error) {
      console.error("Save error:", error)
      this.showSnackbar("Network error. Please try again.", "error")
      this.saveButtonTarget.disabled = false
    } finally {
      this.saveButtonTarget.textContent = "Save All"
    }
  }

  // --- Snackbar ---

  showSnackbar(message, type = "success") {
    const existing = document.querySelector(".edit-content-snackbar")
    if (existing) existing.remove()

    const snackbar = document.createElement("div")
    snackbar.className = `edit-content-snackbar ${type}`
    snackbar.textContent = message
    document.body.appendChild(snackbar)

    requestAnimationFrame(() => snackbar.classList.add("visible"))
    setTimeout(() => {
      snackbar.classList.remove("visible")
      setTimeout(() => snackbar.remove(), 300)
    }, 3000)
  }

  // --- Utilities ---

  throttle(func, limit) {
    let inThrottle
    return (...args) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    }
  }
}
